/**
 * Capture the visible Leaflet slide for Ask AI.
 *
 * html2canvas alone often paints a white map: S3/CDN tiles without
 * `crossOrigin` on <img> are skipped. We:
 *  1) ensure TileLayer / ImageOverlay use CORS and redraw
 *  2) composite visible tile/overlay images (fetching via CORS when needed)
 *  3) fall back to html2canvas without Leaflet chrome
 */

function waitForEvent(target, eventName, timeoutMs = 4000) {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      cleanup();
      resolve();
    };
    const onEvent = () => finish();
    const timer = setTimeout(finish, timeoutMs);
    const cleanup = () => {
      clearTimeout(timer);
      try {
        target.off?.(eventName, onEvent);
      } catch (_) {
        /* ignore */
      }
    };
    try {
      target.once?.(eventName, onEvent);
    } catch (_) {
      finish();
    }
  });
}

function enableCorsOnMapLayers(map) {
  if (!map) return [];
  const layers = [];
  map.eachLayer((layer) => {
    if (!layer) return;
    const isTileLayer =
      typeof layer.getTileUrl === "function" && typeof layer.redraw === "function";
    if (isTileLayer) {
      layer.options = { ...(layer.options || {}), crossOrigin: true };
      layers.push(layer);
      try {
        layer.redraw();
      } catch (_) {
        /* ignore */
      }
      return;
    }
    if (layer._image || typeof layer.setUrl === "function") {
      layer.options = { ...(layer.options || {}), crossOrigin: true };
      try {
        if (layer._image) layer._image.crossOrigin = "anonymous";
        const url = layer._url || layer.getElement?.()?.src;
        if (url && typeof layer.setUrl === "function") layer.setUrl(url, false);
      } catch (_) {
        /* ignore */
      }
    }
  });
  return layers;
}

async function waitForMapTiles(map, layers) {
  if (!map) return;
  const waiters = [];
  (layers || []).forEach((layer) => {
    if (layer && typeof layer.once === "function") {
      waiters.push(waitForEvent(layer, "load", 3500));
    }
  });
  if (!waiters.length) {
    await new Promise((r) => setTimeout(r, 250));
    return;
  }
  await Promise.race([
    Promise.all(waiters),
    new Promise((r) => setTimeout(r, 3500)),
  ]);
  await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 80)));
}

function loadImageElement(src, { crossOrigin = true } = {}) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load ${src}`));
    img.src = src;
  });
}

async function loadDrawableImage(src) {
  if (!src) return null;
  try {
    return await loadImageElement(src, { crossOrigin: true });
  } catch (_) {
    /* fall through — try fetch blob (works when ACAO is present but img lacked crossOrigin) */
  }
  try {
    const res = await fetch(src, { mode: "cors", credentials: "omit" });
    if (!res.ok) return null;
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const img = await loadImageElement(objectUrl, { crossOrigin: false });
    img.__objectUrl = objectUrl;
    return img;
  } catch (_) {
    return null;
  }
}

/**
 * Composite visible tile + overlay images onto a canvas in map-pixel space.
 */
async function compositeTilePaneToCanvas(map) {
  if (!map?.getSize || !map?.getContainer) return null;
  const size = map.getSize();
  if (!size?.x || !size?.y) return null;
  const mapEl = map.getContainer();
  const mapRect = mapEl.getBoundingClientRect();
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(size.x));
  canvas.height = Math.max(1, Math.round(size.y));
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const panes = [map.getPanes()?.tilePane, map.getPanes()?.overlayPane].filter(Boolean);

  const jobs = [];
  panes.forEach((pane) => {
    pane.querySelectorAll("img").forEach((img) => {
      const src = img.currentSrc || img.src;
      if (!src) return;
      const rect = img.getBoundingClientRect();
      const x = rect.left - mapRect.left;
      const y = rect.top - mapRect.top;
      const w = rect.width;
      const h = rect.height;
      if (w < 1 || h < 1) return;
      if (x + w < 0 || y + h < 0 || x > canvas.width || y > canvas.height) return;
      jobs.push({ src, x, y, w, h });
    });
  });

  if (!jobs.length) return null;

  let drew = 0;
  await Promise.all(
    jobs.map(async (job) => {
      const drawable = await loadDrawableImage(job.src);
      if (!drawable) return;
      try {
        ctx.drawImage(drawable, job.x, job.y, job.w, job.h);
        drew += 1;
      } catch (_) {
        /* skip */
      } finally {
        if (drawable.__objectUrl) {
          try {
            URL.revokeObjectURL(drawable.__objectUrl);
          } catch (_) {
            /* ignore */
          }
        }
      }
    })
  );

  if (!drew) return null;
  try {
    return canvas.toDataURL("image/png");
  } catch (_) {
    return null;
  }
}

export async function captureLeafletSnapshot({ map, mapNode, html2canvas }) {
  if (!mapNode) throw new Error("Map not found");

  const layers = enableCorsOnMapLayers(map);
  try {
    map?.invalidateSize?.(true);
  } catch (_) {
    /* ignore */
  }
  await waitForMapTiles(map, layers);

  const composited = map ? await compositeTilePaneToCanvas(map) : null;
  if (composited) return composited;

  if (typeof html2canvas !== "function") {
    throw new Error("Could not capture slide snapshot");
  }

  const canvas = await html2canvas(mapNode, {
    useCORS: true,
    allowTaint: false,
    backgroundColor: "#ffffff",
    scale: window.devicePixelRatio > 1 ? 1.5 : 1,
    logging: false,
    ignoreElements: (el) =>
      !!el?.classList?.contains("leaflet-control-container") ||
      !!el?.classList?.contains("leaflet-control") ||
      !!el?.classList?.contains("leaflet-minimap-container") ||
      !!el?.closest?.(".leaflet-control-container"),
  });
  return canvas.toDataURL("image/png");
}
